import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';

/**
 * In-memory description cache: productId -> cleaned description string
 */
const descriptionCache = new Map();

/**
 * In-flight request tracker to prevent duplicate concurrent network calls for the same ID
 */
const pendingRequests = new Map();

/**
 * Simple concurrency-controlled request queue
 */
const MAX_CONCURRENT = 4;
let activeCount = 0;
const queue = [];

function processQueue() {
    while (activeCount < MAX_CONCURRENT && queue.length > 0) {
        const task = queue.shift();
        activeCount++;
        task()
            .catch(() => {})
            .finally(() => {
                activeCount--;
                processQueue();
            });
    }
}

function enqueue(task) {
    return new Promise((resolve, reject) => {
        queue.push(() =>
            task()
                .then(resolve)
                .catch(reject)
        );
        processQueue();
    });
}

/**
 * Clean UTF-8 mojibake, HTML tags, and invisible Unicode whitespace
 */
export function cleanMojibakeAndEntities(str) {
    if (!str) return '';
    let text = String(str);
    try {
        text = decodeURIComponent(escape(text));
    } catch (e) {
        text = text
            .replace(/ðŸ”©/g, '🔨')
            .replace(/ðŸ”§/g, '🔧')
            .replace(/âš™ï¸/g, '⚙️')
            .replace(/ðŸ§©/g, '🧩')
            .replace(/ðŸ“¦/g, '📦')
            .replace(/âœ…/g, '✅')
            .replace(/â€“/g, '–')
            .replace(/â€”/g, '—')
            .replace(/â€œ|â€ /g, '"')
            .replace(/â€™/g, "'")
            .replace(/â€¢/g, '•')
            .replace(/Â/g, '');
    }
    return text
        .replace(/<[^>]*>?/gm, '')
        .replace(/[\u00ad\u200b-\u200f\ufeff]/g, '')
        .replace(/[\u00a0\u1680\u2000-\u200a\u2028\u2029\u202f\u205f\u3000]/g, ' ')
        .replace(/\r\n/g, '\n')
        .replace(/\r/g, '\n');
}

/**
 * Fallback description formatted from available metadata (Merek, Satuan, Lokasi)
 */
export function getFallbackDescription(item = {}) {
    const parts = [];
    if (item.merek_name) parts.push(`Merek ${item.merek_name}`);
    if (item.satuan) parts.push(`Satuan: ${item.satuan}`);
    if (item.warehouse_name) parts.push(`Pengiriman dari ${item.warehouse_name}`);
    return parts.length > 0
        ? `Produk berkualitas dari Hokky Bangunan. ${parts.join(' • ')}.`
        : 'Produk perlengkapan bangunan berkualitas dan terpercaya di Hokky Bangunan.';
}

/**
 * Extract specifically the clean narrative "Deskripsi Produk" section from raw description
 */
export function extractCleanProductDescription(raw, item = {}) {
    if (!raw) return getFallbackDescription(item);

    let text = cleanMojibakeAndEntities(raw);

    // 1. Remove scraped marketplace metadata intros
    text = text
        .replace(/^.*Deskripsi\s*Produk\s*\(E[‑-]commerce[^\)]*\)[^\n]*/gim, '')
        .replace(/^.*Berikut\s*ringkasan\s*beberapa\s*contoh:[^\n]*/gim, '')
        .replace(/^.*Dari\s*listing[‑\-\s]*listing\s*di\s*marketplace.*$/gim, '')
        .replace(/^.*Atribut\s*Detail.*$/gim, '')
        .replace(/^.*Berdasarkan\s*sumber\s*dari\s*produsen\s*dan\s*toko[^\n]*/gim, '');

    // 2. Try to capture the section explicitly under 'Deskripsi Produk:'
    let descBody = '';
    const descMatch = text.match(
        /Deskripsi\s*Produk\s*:?\s*([\s\S]*?)(?=(?:✅|⭐|📌|🔹|📍|📎|Spesifikasi|Keunggulan|Fitur\s*Khusus|Sebelum\s*memesan|Admin\s*\d|Untuk\s*pengiriman|Happy\s*Shopping|$))/i
    );

    if (descMatch && descMatch[1] && descMatch[1].trim().length > 15) {
        descBody = descMatch[1].trim();
    } else {
        // If no explicit header, extract the paragraph preceding specifications/disclaimers
        const beforeSpecs = text.split(
            /(?=✅|⭐|📌|🔹|📍|📎|Spesifikasi\s*Produk|Keunggulan\s*Produk|Fitur\s*Khusus|Sebelum\s*memesan|Admin\s*\d)/i
        )[0];
        descBody = beforeSpecs ? beforeSpecs.trim() : '';
    }

    // 3. Remove leading title lines that start with emojis or product names
    descBody = descBody
        .replace(/^[🔩🔨🔧⚙️📦✅⭐\-\s\*\#]+[^\n]*\n+/g, '')
        .replace(/^Deskripsi\s*Produk\s*:?\s*/i, '');

    // 4. Filter out scraped citation artifacts (e.g. '+3', marketplace names)
    const filteredLines = descBody
        .split('\n')
        .map((line) => line.trim())
        .filter((line) => {
            if (!line) return false;
            if (/^\+\s*\d+$/.test(line)) return false;
            const lower = line.toLowerCase();
            const blacklist = [
                'blibli',
                'tokopedia',
                'shopee',
                'lazada',
                'bukalapak',
                'monotaro',
                'gemilang store',
                'klopot',
                'roma bangunan',
            ];
            if (blacklist.some((b) => lower === b || lower === `${b} store`)) return false;
            return true;
        });

    let clean = filteredLines.join(' ');
    clean = clean.replace(/\s+/g, ' ').trim();

    if (clean.length < 15) {
        return getFallbackDescription(item);
    }

    return clean;
}

/**
 * Fetch and extract product description by product ID (cached)
 */
export async function fetchProductDescription(productId, fallbackItem = {}) {
    if (!productId) return getFallbackDescription(fallbackItem);

    // 1. Check in-memory cache
    if (descriptionCache.has(productId)) {
        return descriptionCache.get(productId);
    }

    // 2. Check browser sessionStorage
    if (typeof window !== 'undefined') {
        try {
            const saved = window.sessionStorage?.getItem(`prod_desc_${productId}`);
            if (saved) {
                descriptionCache.set(productId, saved);
                return saved;
            }
        } catch (e) {}
    }

    // 3. Return existing in-flight promise if already requested
    if (pendingRequests.has(productId)) {
        return pendingRequests.get(productId);
    }

    // 4. Enqueue API request
    const requestPromise = enqueue(async () => {
        try {
            const res = await axios.get(
                `${process.env.REACT_APP_HOST_API}/items-searching/get/${productId}`
            );
            const data = res.data?.response;
            const rawDesc = data?.item_description || data?.description || data?.deskripsi;
            const cleanDesc = extractCleanProductDescription(rawDesc, fallbackItem || data);

            descriptionCache.set(productId, cleanDesc);
            if (typeof window !== 'undefined') {
                try {
                    window.sessionStorage?.setItem(`prod_desc_${productId}`, cleanDesc);
                } catch (e) {}
            }
            return cleanDesc;
        } catch (err) {
            const fallback = getFallbackDescription(fallbackItem);
            descriptionCache.set(productId, fallback);
            return fallback;
        } finally {
            pendingRequests.delete(productId);
        }
    });

    pendingRequests.set(productId, requestPromise);
    return requestPromise;
}

/**
 * Background prefetch for a list of products
 */
export function prefetchProductDescriptions(items = []) {
    if (!Array.isArray(items) || items.length === 0) return;
    items.forEach((it) => {
        const id = it?.id || it?.id_is;
        if (!id) return;
        if (it?.item_description) {
            const clean = extractCleanProductDescription(it.item_description, it);
            descriptionCache.set(id, clean);
            return;
        }
        if (!descriptionCache.has(id)) {
            fetchProductDescription(id, it);
        }
    });
}

/**
 * Custom React Hook for components to consume clean product description
 */
export function useProductDescription(product) {
    const productId = product?.id || product?.id_is;

    const initialDesc = useMemo(() => {
        if (!product) return '';
        if (product.item_description) {
            return extractCleanProductDescription(product.item_description, product);
        }
        if (productId && descriptionCache.has(productId)) {
            return descriptionCache.get(productId);
        }
        if (productId && typeof window !== 'undefined') {
            try {
                const saved = window.sessionStorage?.getItem(`prod_desc_${productId}`);
                if (saved) {
                    descriptionCache.set(productId, saved);
                    return saved;
                }
            } catch (e) {}
        }
        return getFallbackDescription(product);
    }, [productId, product?.item_description]);

    const [description, setDescription] = useState(initialDesc);

    useEffect(() => {
        if (!productId) return;

        // If product already has item_description, format immediately
        if (product?.item_description) {
            const clean = extractCleanProductDescription(product.item_description, product);
            setDescription(clean);
            return;
        }

        // If already cached, apply immediately
        if (descriptionCache.has(productId)) {
            setDescription(descriptionCache.get(productId));
            return;
        }

        let isMounted = true;
        fetchProductDescription(productId, product).then((cleanDesc) => {
            if (isMounted && cleanDesc) {
                setDescription(cleanDesc);
            }
        });

        return () => {
            isMounted = false;
        };
    }, [productId, product]);

    return description;
}
